import { Link } from "react-router-dom";
import { fileUrl } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { MapPin } from "lucide-react";
import SaveButton from "@/components/SaveButton";
import VerifiedBadge from "@/components/VerifiedBadge";

const PLACEHOLDER = "https://images.unsplash.com/photo-1572521165329-b197f9ea3da6?crop=entropy&cs=srgb&fm=jpg&w=800&q=70&ixlib=rb-4.1.0";

function currencySymbol(c) {
  return c === "GBP" ? "£" : c === "USD" ? "$" : c === "EUR" ? "€" : c + " ";
}

export default function ListingCard({ listing, testIdPrefix = "listing" }) {
  const cover = listing.images?.[0] ? fileUrl(listing.images[0]) : PLACEHOLDER;
  return (
    <Link
      to={`/listings/${listing.listing_id}`}
      data-testid={`${testIdPrefix}-card-${listing.listing_id}`}
      className="group block border border-border bg-white transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg"
    >
      <div className="relative aspect-[4/3] overflow-hidden bg-sage">
        <img
          src={cover}
          alt={listing.title}
          onError={(e) => { e.currentTarget.src = PLACEHOLDER; }}
          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
        />
        <div className="absolute top-3 left-3 flex gap-2">
          {listing.is_featured && (
            <Badge className="rounded-none bg-forest text-white border-0 overline text-[10px]">Featured</Badge>
          )}
          {listing.is_clearance && (
            <Badge className="rounded-none bg-safety text-white border-0 overline text-[10px]">Clearance</Badge>
          )}
        </div>
        <div className="absolute top-3 right-3">
          <SaveButton
            listingId={listing.listing_id}
            testId={`listing-save-${listing.listing_id}`}
            className="w-9 h-9 bg-black/40 hover:bg-black/60 backdrop-blur-sm"
          />
        </div>
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4">
          <div className="text-white font-display text-xl font-medium tabular-nums">
            {currencySymbol(listing.currency)}{Number(listing.price).toFixed(2)}
            {listing.quantity > 1 && (
              <span className="text-xs text-white/70 ml-2 overline">×{listing.quantity}</span>
            )}
          </div>
        </div>
      </div>
      <div className="p-5">
        <div className="overline text-muted-foreground text-[10px] mb-2">{listing.category} · {listing.condition}</div>
        <h3 className="font-display text-lg tracking-tight line-clamp-2 leading-snug group-hover:text-forest transition-colors duration-200">
          {listing.title}
        </h3>
        <div className="mt-4 flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1.5">
            <MapPin className="w-3.5 h-3.5" />
            <span className="truncate max-w-[120px]">{listing.location}</span>
          </div>
          <div className="flex items-center gap-1.5 truncate max-w-[160px]">
            <VerifiedBadge verified={listing.seller_is_verified} size="sm" testId={`listing-verified-${listing.listing_id}`} />
            <span className="truncate">{listing.seller_name}</span>
          </div>
        </div>
      </div>
    </Link>
  );
}
